import React, { useContext, useEffect, useState } from 'react';
import { addMinutes, format } from 'date-fns';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import timezones from 'timezones-list';
import { useMutation } from '@apollo/client';

// Components
import TimeSlotsPanel from '../../_molecules/TimeSlotsPanel';
import Button from '@atoms/Button';
import Calendar from '@molecules/Calendar';
import MemberListItemRedesign from '@molecules/MemberListItemRedesign';
import { DividerLightGreyStyled, H3, H4, H5, LabelLarge, Textarea, Textbox } from '@styled';

// Helpers
import useEventSlots from '../../../hooks/useEventSlots';
import useTimeSlots from '../../../hooks/useTimeSlots';
import { GlobalModalContext } from '@context/globalModalContext';
import { CREATE_APPOINTMENT } from '@general/graphql/appointments.graph';
import { availabilityStatuses } from '@general/helpers/scheduling.helpers';
import { mediaQueries } from '@general/breakpoints';
import { useScreenMinMd } from '@hooks/useResponsive';
import useIcscTracking from '@tracking/useIcscTracking';

const BodyContainer = styled.div`
    @media ${mediaQueries.md} {
        display: flex;
    }
`;

const CalendarContainer = styled.div`
    width: 100%;

    @media ${mediaQueries.md} {
        margin-right: 62px;
        min-width: 448px;
        width: 448px;
    }
`;

const TimeSlotsPanelContainer = styled.div`
    margin-top: 24px;

    @media ${mediaQueries.md} {
        margin-top: 0;
    }
`;

const timeZoneOptions = [
    {
        ...timezones.find(timezone => timezone.tzCode === 'America/New_York'),
        label: 'Eastern Time (UTC-05:00)',
        value: 'EST',
    },
    {
        ...timezones.find(timezone => timezone.tzCode === 'America/Chicago'),
        label: 'Central Time (UTC-06:00)',
        value: 'CST',
    },
    {
        ...timezones.find(timezone => timezone.tzCode === 'America/Phoenix'),
        label: 'Mountain Time (UTC-07:00)',
        value: 'MST',
    },
    {
        ...timezones.find(timezone => timezone.tzCode === 'America/Los_Angeles'),
        label: 'Pacific Time (UTC-08:00)',
        value: 'PST',
    },
];

const easternTimeZone = timeZoneOptions[0];

export const useScheduleAppointmentModal = ({ event, timeSlotDuration }) => {
    const [, setIsModalOpen, setModalChildren, setModalOuterClass] = useContext(GlobalModalContext);

    useEffect(() => {
        setModalOuterClass('schedule-appointment-modal');
    }, [setModalOuterClass]);

    const openModal = (member, index) => {
        setModalChildren(() => (
            <ScheduleAppointmentModal
                event={event}
                index={index}
                member={member}
                timeSlotDuration={timeSlotDuration}
                closeModal={() => setIsModalOpen(false)}
            />
        ));

        setIsModalOpen(true);
    };

    return { openModal };
};

const ScheduleAppointmentModal = ({ event, index, member, timeSlotDuration = 30, closeModal }) => {
    const { title, meeting_start_time: eventStart, meeting_end_time: eventEnd } = event;
    const { firstName, lastName, graphID } = member;

    // -----------------------------
    // State
    // -----------------------------

    const [activeDate, setActiveDate] = useState(event.meeting_start_time);
    const [eventTimeZone, setEventTimeZone] = useState(easternTimeZone);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [location, setLocation] = useState('');
    const [message, setMessage] = useState('');
    const { startTime, endTime } = useEventSlots({ eventCode: event.event_code, eventDay: activeDate });
    const timeSlots = useTimeSlots(startTime, endTime, timeSlotDuration);
    const isScreenMinMd = useScreenMinMd();
    const { trackEvent } = useIcscTracking();

    useEffect(() => {
        if (!event.timezone) {
            return;
        }

        setEventTimeZone(timeZoneOptions.find(tzOption => tzOption.tzCode === event.timezone) || easternTimeZone);
    }, [event]);

    // -----------------------------
    // Helpers
    // -----------------------------

    const [createAppointment, { loading: createAppointmentSubmitting }] = useMutation(CREATE_APPOINTMENT);
    const submitRequest = async () => {
        if (!selectedTimeSlot) {
            alert('Please select a time slot');
            return;
        }

        await createAppointment({
            variables: {
                eventName: title,
                recipients: [graphID],
                beginsAt: selectedTimeSlot.start.toISOString(),
                endsAt: addMinutes(selectedTimeSlot.start, timeSlotDuration).toISOString(),
                location: location || 'N/A',
                message: message || 'Empty',
            },
        });

        closeModal();
        trackEvent({
            click: {
                type: 'conversion',
                index: `${process.env.ALGOLIA_ENV_PREFIX}_${index}`,
                hit: member,
                target: 'submit_appointment_request_modal_button',
                value: 'Connect With Attendees',
            },
        });
    };

    return (
        <div>
            <LabelLarge className="text-primary">Schedule an appointment</LabelLarge>
            <H3 className="mt-2 mb-2rem">
                Meet with {firstName} {lastName}
            </H3>

            <MemberListItemRedesign {...member} showMailIcon={false} />
            <DividerLightGreyStyled className="mb-2rem" />

            <div>
                <H4 className="mb-2rem">Select a date and time to meet</H4>

                <BodyContainer className="mb-2rem">
                    <CalendarContainer>
                        <Calendar
                            activeDate={activeDate}
                            allowedDateRange={{ startDate: eventStart, endDate: eventEnd }}
                            onActiveDateChange={setActiveDate}
                        />
                    </CalendarContainer>

                    <TimeSlotsPanelContainer>
                        <TimeSlotsPanel
                            columns={isScreenMinMd ? 5 : 2}
                            header={format(activeDate, 'EEEE, MMMM d')}
                            subheader={`${timeSlotDuration} Minute Appointments - All times shown in ${eventTimeZone.label}`}
                            selectedTimeSlot={selectedTimeSlot}
                            timeSlots={timeSlots.map(timeSlot => ({
                                ...timeSlot,
                                availability: availabilityStatuses.AVAILABLE,
                            }))}
                            timeZone={eventTimeZone}
                            onTimeSlotClick={setSelectedTimeSlot}
                        />

                        <DividerLightGreyStyled className="mt-2.25rem mb-2rem" />

                        <div>
                            <H5 className="mb-1rem md:mb-2rem">Where would you like to meet?</H5>

                            <div className="mb-0.5rem">
                                <LabelLarge>Meeting Location</LabelLarge>
                            </div>

                            <div>
                                <Textbox
                                    placeholder="Location Name"
                                    value={location}
                                    onChange={e => setLocation(e.target.value)}
                                />
                            </div>
                        </div>
                    </TimeSlotsPanelContainer>
                </BodyContainer>
            </div>

            <DividerLightGreyStyled className="mb-2rem" />

            <div className="mb-2rem">
                <H4 className="mb-2rem">Include a message with your appointment request.</H4>
                <Textarea placeholder="" value={message} onChange={e => setMessage(e.target.value)} />
            </div>

            <div className="d-flex">
                <Button
                    buttonClasses="appointment"
                    disabled={createAppointmentSubmitting}
                    onClick={() => submitRequest()}
                >
                    Submit Appointment Request
                </Button>
            </div>
        </div>
    );
};

ScheduleAppointmentModal.propTypes = {
    event: PropTypes.shape({
        title: PropTypes.string.isRequired,
        event_code: PropTypes.string.isRequired,
        meeting_start_time: PropTypes.instanceOf(Date),
        meeting_end_time: PropTypes.instanceOf(Date),
        timezone: PropTypes.string,
    }),
    eventHoursByDay: PropTypes.array,
    index: PropTypes.string.isRequired,
    timeSlotDuration: PropTypes.number,
    member: PropTypes.shape({
        graphID: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
    }).isRequired,
    closeModal: PropTypes.func,
    onSchedule: PropTypes.func,
};

export default ScheduleAppointmentModal;
